import { DotsVerticalIcon } from '@vizit/icons';
import { cva, cx } from '@vizit/theme';
import { Button, ButtonLevel } from '../button';
import { MenuTrigger } from '../menu';

interface ContextMenuProps extends React.ComponentPropsWithoutRef<typeof MenuTrigger> {
  /**
   * ref to be passed to MenuPopover.
   *
   * @optional
   * @type boolean
   * @default false
   */
  ref?: React.RefObject<HTMLDivElement>;
  /**
   * Props to be passed to the trigger button.
   *
   * @optional
   * @type {isHovered: boolean, isMenuOpen: boolean, isLoading: boolean} & React.ComponentPropsWithoutRef<typeof Button>
   * @default {}
   */
  triggerProps?: {
    isHovered?: boolean;
    isMenuOpen?: boolean;
    isLoading?: boolean;
  } & React.ComponentPropsWithoutRef<typeof Button>;
}

const menuTriggerClasses = cva({
  base: [
    'flex items-center justify-center w-9 h-9 sm:w-18 sm:h-18 rounded-none px-0 py-0',
    'group',
    'bg-overlay-action hover:bg-overlay-action-hover border-none',
  ],
  variants: {
    isHovered: {
      true: 'opacity-100',
      false: 'opacity-0',
    },
  },
  defaultVariants: {
    isHovered: false,
  },
});

function ContextMenu(props: ContextMenuProps) {
  const { children, triggerProps = {}, ...other } = props;
  const { isHovered, isMenuOpen, isLoading, className, ...otherTriggerProps } = triggerProps;

  return (
    <MenuTrigger {...other}>
      <Button
        className={cx(menuTriggerClasses({ isHovered: isHovered || isMenuOpen }), className)}
        isDisabled={isLoading}
        level={ButtonLevel.Ghost}
        aria-label="Open menu"
        {...otherTriggerProps}
      >
        <DotsVerticalIcon className="text-quinary group-hover:text-quinary-hover" />
      </Button>
      {children}
    </MenuTrigger>
  );
}

export { ContextMenu };
